import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Row, Col, Skeleton } from 'antd';
import PropTypes from 'prop-types';

import { GET_ALL_MEDIAS_LIBRARIES } from 'graphql/queries/contentLibraryQueries';
import helpers from 'helpers';
import EmptyFolderIcon from 'components/Icons/EmptyFolderIcon';
import GreenTickIcon from 'components/Icons/GreenTickIcon';
import HyphenRoundIcon from 'components/Icons/HyphenRoundIcon';

import './LibraryFolderItems.scss';

/**
 * LibraryFolderItems component
 * @param {Object} props - The component props
 * @param {string} props.selectedFolder - The selected folder
 * @param {Object} props.selectedItems - The selected items object
 * @param {Function} props.addToSeletedItem - The add to selected item function
 * @param {Array} props.showFileTypes - The show file types array
 * @param {number} props.span - The span
 * @param {string} props.coverType - The cover type
 * @param {boolean} props.multiSelect - Whether to show multiple items
 * @returns {JSX.Element} The LibraryFolderItems component
 */
const LibraryFolderItems = ({
  selectedFolder,
  selectedItems,
  addToSeletedItem,
  showFileTypes = [],
  span,
  coverType,
  multiSelect,
}) => {
  const [loadMedias, { data: mediasData, loading }] = useLazyQuery(GET_ALL_MEDIAS_LIBRARIES, {
    variables: {
      type: 'medias',
      ...(selectedFolder === 'all-starred'
        ? { allStarred: true }
        : { key: selectedFolder === 'all' ? '' : selectedFolder }),
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (selectedFolder) {
      loadMedias({
        variables: {
          type: 'medias',
          ...(selectedFolder === 'all-starred'
            ? { allStarred: true }
            : { key: selectedFolder === 'all' ? '' : selectedFolder }),
        },
      });
    }
  }, [selectedFolder, loadMedias]);

  const medias = (mediasData?.fetchContentLibraryMediasItems?.slice() || []).filter((m) =>
    helpers.isImage(m.src)
  );

  const renderSkeletons = () =>
    [1, 2, 3].map((key) => (
      <Col key={key}>
        <Skeleton.Avatar active shape="square" size={170} />
      </Col>
    ));

  return (
    <Row gutter={[12, 12]} className="library-folder-items">
      {loading ? (
        renderSkeletons()
      ) : (
        <>
          {medias.length > 0 ? (
            medias.map((media) => (
              <Media
                media={media}
                addToSeletedItem={addToSeletedItem}
                selectedItems={selectedItems}
                showFileTypes={showFileTypes}
                span={span}
              />
            ))
          ) : (coverType && coverType !== 'covers') || multiSelect ? (
            <EmptyFolderIcon className="empty-folder-icon" />
          ) : null}
        </>
      )}
    </Row>
  );
};

LibraryFolderItems.defaultProps = {
  selectedFolder: '',
  selectedItems: {},
  addToSeletedItem: () => {},
  showFileTypes: [],
  span: 12,
  coverType: '',
  multiSelect: false,
};

LibraryFolderItems.propTypes = {
  selectedFolder: PropTypes.string,
  selectedItems: PropTypes.object,
  addToSeletedItem: PropTypes.func,
  showFileTypes: PropTypes.array,
  span: PropTypes.number,
  coverType: PropTypes.string,
  multiSelect: PropTypes.bool,
};

export default LibraryFolderItems;

/**
 * Media component
 * @param {Object} props - The component props
 * @param {Object} props.media - The media object
 * @param {Function} props.addToSeletedItem - The add to selected item function
 * @param {Object} props.selectedItems - The selected items object
 * @param {Array} props.showFileTypes - The show file types array
 * @param {number} props.span - The span
 */
const Media = ({ media, addToSeletedItem, selectedItems, showFileTypes, span }) => {
  const extension = media?.src.split('.').pop().toLowerCase() || '';

  if (showFileTypes.length && !showFileTypes.includes(extension)) {
    return null;
  }

  return (
    <Col span={span} className="folder-item">
      {selectedItems[media.src] && (
        <>
          <HyphenRoundIcon onClick={() => addToSeletedItem(media)} className="remove-icon" />
          <GreenTickIcon className="tick-icon" />
        </>
      )}
      <img
        width="100%"
        height="100px"
        src={media.src}
        alt={media.name}
        className="item-img"
        onClick={() => addToSeletedItem(media)}
      />
    </Col>
  );
};
